/**
 * Gemini 3.1 Flash TTS (client-side REST preview).
 * Uses Google AI Studio / Generative Language API with browser env key.
 */

import { GEMINI_LIVE_DEFAULT_VOICE } from './geminiLiveVoices';
import {
  TTS_PLAYBACK_POST_END_PAD_MS,
  TTS_AUDIO_TAIL_EPSILON_SEC,
  TTS_PREMATURE_TAIL_POLL_MS,
  TTS_PREMATURE_TAIL_MAX_EXTRA_MS,
} from '../ttsService';

const DEFAULT_VOICE = GEMINI_LIVE_DEFAULT_VOICE || 'Aoede';

const GEMINI_FLASH_TTS_MODEL = 'gemini-3.1-flash-preview-tts';

function getGeminiFlashTtsApiKey() {
  return (typeof process !== 'undefined' && process.env.REACT_APP_GOOGLE_AI_API_KEY
    ? String(process.env.REACT_APP_GOOGLE_AI_API_KEY).trim()
    : '');
}

/**
 * @typedef {Object} GeminiFlashTTSConfig
 * @property {string} [apiKey]
 * @property {string} [voiceName]
 */

class GeminiFlashTTSService {
  constructor() {
    /** @type {GeminiFlashTTSConfig} */
    this.config = {
      apiKey: '',
      voiceName: DEFAULT_VOICE,
    };
    this.currentAudio = null;
    this._isSpeaking = false;
    this.onStart = null;
    this.onEnd = null;
    this.onError = null;
    this._postEndPadTimer = null;
    this._completePostEndPlay = null;
    this._tailRecoveryPollId = null;
    this._playbackToken = 0;
  }

  _clearTailRecoveryPoll() {
    if (this._tailRecoveryPollId !== null) {
      clearInterval(this._tailRecoveryPollId);
      this._tailRecoveryPollId = null;
    }
  }

  /**
   * @param {GeminiFlashTTSConfig} config
   */
  configure(config = {}) {
    this.config = {
      ...this.config,
      ...config,
      voiceName:
        typeof config.voiceName === 'string' && config.voiceName.trim()
          ? config.voiceName.trim()
          : this.config.voiceName || DEFAULT_VOICE,
    };
    if (typeof config.apiKey === 'string') {
      this.config.apiKey = config.apiKey.trim();
    }
  }

  /**
   * @param {{ onStart?: function, onEnd?: function, onError?: function }} handlers
   */
  setHandlers(handlers) {
    if (handlers.onStart) this.onStart = handlers.onStart;
    if (handlers.onEnd) this.onEnd = handlers.onEnd;
    if (handlers.onError) this.onError = handlers.onError;
  }

  /**
   * @param {string} text
   * @param {Object} [options]
   * @param {string} [options.voiceName] - Override prebuilt voice for this request
   * @returns {Promise<string>} Blob URL (same contract as {@link deepgramTTSService.generateSpeech})
   */
  async generateSpeech(text, options = {}) {
    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      throw new Error('Text cannot be empty');
    }

    const apiKey = (options.apiKey ?? this.config.apiKey ?? getGeminiFlashTtsApiKey()).trim();
    if (!apiKey) {
      const err = new Error('Gemini TTS: API key not configured');
      throw err;
    }

    const voiceName =
      (typeof options.voiceName === 'string' && options.voiceName.trim()
        ? options.voiceName.trim()
        : null) || this.config.voiceName || DEFAULT_VOICE;

    const url = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_FLASH_TTS_MODEL}:generateContent?key=${encodeURIComponent(apiKey)}`;

    const body = {
      contents: [{ parts: [{ text: text.trim() }] }],
      generationConfig: {
        responseModalities: ['AUDIO'],
        speechConfig: {
          voiceConfig: {
            prebuiltVoiceConfig: { voiceName },
          },
        },
      },
    };

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      throw new Error(`Gemini TTS error: ${res.status}`);
    }

    let json;
    try {
      json = await res.json();
    } catch (e) {
      throw new Error('Gemini TTS error: invalid JSON response');
    }

    const b64 = json?.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;

    if (!b64 || typeof b64 !== 'string') {
      throw new Error('Gemini TTS: No audio in response');
    }

    const binaryString = atob(b64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'audio/wav' });
    return URL.createObjectURL(blob);
  }

  /**
   * @param {string} text
   * @param {Object} [options]
   * @param {function} [options.onStart]
   * @param {function} [options.onEnd]
   * @param {function} [options.onError]
   * @param {string} [options.voiceName]
   * @returns {Promise<void>}
   */
  async speak(text, options = {}) {
    if (this._isSpeaking) {
      this.stop();
    }

    const onStartCb = options.onStart ?? this.onStart;
    const onEndCb = options.onEnd ?? this.onEnd;
    const onErrorCb = options.onError ?? this.onError;

    try {
      const blobUrl = await this.generateSpeech(text, options);
      await this.playAudio(blobUrl, { onStart: onStartCb, onEnd: onEndCb, onError: onErrorCb });
    } catch (error) {
      console.error('[GeminiFlashTTS] speak() error:', error);
      onErrorCb?.(error);
      throw error;
    }
  }

  /**
   * @param {string} audioUrl
   * @param {{ onStart?: function, onEnd?: function, onError?: function }} callbacks
   * @returns {Promise<void>}
   */
  playAudio(audioUrl, callbacks = {}) {
    const { onStart, onEnd, onError } = callbacks;
    return new Promise((resolve, reject) => {
      this.stop();
      const playbackToken = ++this._playbackToken;

      this.currentAudio = new Audio(audioUrl);

      this.currentAudio.onplay = () => {
        if (playbackToken !== this._playbackToken) return;
        this._isSpeaking = true;
        onStart?.();
      };

      this.currentAudio.volume = 1.0;
      this.currentAudio.muted = false;

      this.currentAudio.onended = () => {
        if (playbackToken !== this._playbackToken) return;
        const runCompletion = () => {
          if (playbackToken !== this._playbackToken) return;
          if (this._postEndPadTimer) {
            clearTimeout(this._postEndPadTimer);
            this._postEndPadTimer = null;
          }
          this._completePostEndPlay = null;
          this._clearTailRecoveryPoll();
          this._isSpeaking = false;
          this.currentAudio = null;
          onEnd?.();
          resolve();
        };

        const queuePostEndPad = () => {
          this._completePostEndPlay = runCompletion;
          this._postEndPadTimer = setTimeout(runCompletion, TTS_PLAYBACK_POST_END_PAD_MS);
        };

        const el = this.currentAudio;
        const dur = el?.duration;
        const ct = el?.currentTime;
        const eps = TTS_AUDIO_TAIL_EPSILON_SEC;
        const looksTruncated =
          el &&
          Number.isFinite(dur) &&
          dur > eps &&
          Number.isFinite(ct) &&
          ct < dur - eps;

        if (looksTruncated) {
          console.warn('[GeminiFlashTTS] HTMLAudio ended before duration tail; recovering playback', {
            duration: dur,
            currentTime: ct,
          });
          this._clearTailRecoveryPoll();
          void el.play().catch(() => {});
          const started = Date.now();
          const rate = Math.max(0.25, el.playbackRate || 1);
          const maxWaitMs = Math.min(
            TTS_PREMATURE_TAIL_MAX_EXTRA_MS,
            ((dur - ct) / rate) * 1000 + 2500
          );
          this._tailRecoveryPollId = window.setInterval(() => {
            if (this.currentAudio !== el) {
              this._clearTailRecoveryPoll();
              queuePostEndPad();
              return;
            }
            const ct2 = el.currentTime;
            const dur2 = el.duration;
            if (
              !Number.isFinite(dur2) ||
              dur2 <= eps ||
              (Number.isFinite(ct2) && ct2 >= dur2 - eps)
            ) {
              this._clearTailRecoveryPoll();
              queuePostEndPad();
              return;
            }
            if (Date.now() - started > maxWaitMs) {
              console.warn('[GeminiFlashTTS] Tail recovery timed out; completing TTS', {
                currentTime: ct2,
                duration: dur2,
              });
              this._clearTailRecoveryPoll();
              queuePostEndPad();
            }
          }, TTS_PREMATURE_TAIL_POLL_MS);
          return;
        }

        queuePostEndPad();
      };

      this.currentAudio.onerror = () => {
        if (playbackToken !== this._playbackToken) return;
        if (this._postEndPadTimer) {
          clearTimeout(this._postEndPadTimer);
          this._postEndPadTimer = null;
        }
        this._completePostEndPlay = null;
        this._clearTailRecoveryPoll();
        this._isSpeaking = false;
        this.currentAudio = null;
        const err = new Error('Audio playback failed');
        onError?.(err);
        reject(err);
      };

      this.currentAudio.play().catch((error) => {
        if (playbackToken !== this._playbackToken) return;
        if (this._postEndPadTimer) {
          clearTimeout(this._postEndPadTimer);
          this._postEndPadTimer = null;
        }
        this._completePostEndPlay = null;
        this._clearTailRecoveryPoll();
        this._isSpeaking = false;
        this.currentAudio = null;
        onError?.(error);
        reject(error);
      });
    });
  }

  stop() {
    this._playbackToken += 1;
    this._clearTailRecoveryPoll();
    if (this._postEndPadTimer) {
      clearTimeout(this._postEndPadTimer);
      this._postEndPadTimer = null;
    }
    if (typeof this._completePostEndPlay === 'function') {
      const finish = this._completePostEndPlay;
      this._completePostEndPlay = null;
      finish();
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
      this._isSpeaking = false;
    }
  }

  /** @returns {boolean} */
  speaking() {
    return this._isSpeaking;
  }

  /** @returns {boolean} */
  get isSpeaking() {
    return this._isSpeaking;
  }
}

export const geminiFlashTTSService = new GeminiFlashTTSService();

export { GeminiFlashTTSService };

export default geminiFlashTTSService;
